/** Wire shapes mirror crates/protocol and crates/sim. Numbers and gameplay rules remain server-owned. */
export type Spell =
  "fireball" | "dash" | "teleport" | "shield" | "meteor" | "dome" | "lightning";
export type Mode = "ffa" | "teams";
export type Phase =
  "countdown" | "combat" | "round_result" | "shop" | "match_over";
export interface PlayerInput {
  sequence: number;
  movement: { x: number; y: number };
  aim: { x: number; y: number };
  cast: [boolean, boolean, boolean, boolean];
}
export interface InputTimeline {
  controller?: "human" | "bot";
  controller_start_tick?: number;
  canceled_ticks?: number[];
  player: number;
  epoch: number;
  base_sequence: number;
  base_tick: number;
  resolved_sequence: number;
  resolved_tick: number;
  commands: { tick: number; input: PlayerInput }[];
}
export interface SpellOffer {
  spell: Spell;
  price: number;
  cooldownSeconds: number;
  damage?: number;
  config?: SpellConfig;
  upgrades?: SpellUpgrade[];
}
export interface SpellConfig {
  price: number;
  cooldown_seconds: number;
  release_seconds: number;
  range: number;
  radius: number;
  speed: number;
  push: number;
  duration_seconds: number;
  height: number;
  speed_multiplier: number;
}
export interface SpellUpgrade {
  rank: number;
  price: number;
  unlockRound: number;
  damage?: number;
  config: SpellConfig;
}
export interface UpgradeReceipt {
  request_id: number;
  player: number;
  round: number;
  spell: Spell;
  slot: number;
  rank_before: number;
  rank_after: number;
  cost: number;
  gold_remaining: number;
}
export interface Member {
  playerId: number;
  name: string;
  bot: boolean;
  connected: boolean;
  team: number | null;
  botDifficulty?: "easy" | "normal" | "hard";
}
export interface Slot {
  index: number;
  open: boolean;
  member: Member | null;
}
export interface Room {
  id: string;
  kind: "friends" | "public";
  mode: Mode;
  leaderId: number;
  slots: Slot[];
  observers: Member[];
  started: boolean;
  canStart: boolean;
  invitePath: string;
}
export interface Player {
  id: number;
  name: string;
  team: number | null;
  bot: boolean;
  alive: boolean;
  health: number;
  max_health: number;
  in_lava: boolean;
  death_cause: "lava" | "spell" | null;
  gold: number;
  round_wins: number;
  eliminations: number;
  loadout: (Spell | null)[];
  loadout_revision?: number;
  cooldowns: number[];
  upgrade_ranks?: number[];
  ready: boolean;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  shield_ticks: number;
}
export interface MatchEvent {
  id: number;
  tick: number;
  kind: { type: string; [key: string]: any };
}
export interface Snapshot {
  tick: number;
  tick_rate: number;
  round: number;
  total_rounds: number;
  mode: Mode;
  phase: Phase;
  phase_ticks_remaining: number;
  round_tick: number;
  arena_radius: number;
  initial_arena_radius: number;
  collapse_warning: number | null;
  players: Player[];
  events: MatchEvent[];
  input_timelines?: InputTimeline[];
  team_scores: { team: number; round_wins: number }[];
  winners: number[];
  gold_receipts?: {
    id: number;
    player: number;
    amount: number;
    victim: number | null;
    round: number;
  }[];
  purchase_receipts?: {
    request_id: number;
    player: number;
    round: number;
    spell: Spell;
    slot: number;
    cost: number;
    gold_remaining: number;
  }[];
  upgrade_receipts?: UpgradeReceipt[];
}
export interface AppState {
  screen: "home" | "loader" | "room" | "match";
  name: string;
  playerId: number;
  matchPlayerId: number | null;
  online: number | null;
  connected: boolean;
  reconnecting: boolean;
  room: Room | null;
  snapshot: Snapshot | null;
  catalog: SpellOffer[];
  engineReady: boolean;
  engineError: string | null;
  followedPlayerId: number | null;
  menuOpen: boolean;
  pendingSpell: Spell | null;
  pendingRequest: number | null;
  pendingReorder: boolean;
  latency: number;
  fps: number;
  selectedSlot: number;
}
export const INITIAL_STATE: AppState = {
  screen: "home",
  name: "",
  playerId: 0,
  matchPlayerId: null,
  online: null,
  connected: false,
  reconnecting: false,
  room: null,
  snapshot: null,
  catalog: [],
  engineReady: false,
  engineError: null,
  followedPlayerId: null,
  menuOpen: false,
  pendingSpell: null,
  pendingRequest: null,
  pendingReorder: false,
  latency: 0,
  fps: 0,
  selectedSlot: 1,
};
